import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, RefreshCw, Wand2, ChevronDown, ChevronRight } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const LINKS = { customers: "/master", projects: "/master", pos: "/program", invoices: "/program", dispatches: "/dispatch", production: "/production", ctb: "/scm", sales: "/sales", pnl: "/pnl" };
const TD = "border border-slate-200 px-2 py-1 align-middle";

function Section({ s, open, toggle }) {
  const ok = s.missing === 0;
  return (
    <div className={`rounded-md border ${ok ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`} data-testid={`fg-health-${s.key}`}>
      <button className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left" onClick={toggle} disabled={ok} data-testid={`fg-health-toggle-${s.key}`}>
        <span className="flex items-center gap-2 text-sm">{ok ? <ShieldCheck size={15} className="text-emerald-600" /> : open ? <ChevronDown size={15} className="text-amber-700" /> : <ChevronRight size={15} className="text-amber-700" />}<span className="font-medium">{s.label}</span></span>
        <span className={`font-mono text-xs font-bold ${ok ? "text-emerald-700" : "text-amber-800"}`} data-testid={`fg-health-count-${s.key}`}>{ok ? `all ${s.total} OK` : `${s.missing} of ${s.total} missing`}</span>
      </button>
      {open && !ok && (
        <div className="px-3 pb-3">
          <div className="text-[11px] text-amber-900 mb-2">Fix: {s.fix} · <Link to={LINKS[s.key]} className="underline font-semibold">open module</Link></div>
          <div className="max-h-64 overflow-auto"><table className="w-full text-xs border-collapse bg-white"><tbody>
            {s.items.map((it) => <tr key={it.id}><td className={`${TD} font-medium`}>{it.a}</td><td className={TD}>{it.b}</td><td className={`${TD} font-mono text-slate-600`}>{it.c}</td><td className={`${TD} font-mono text-slate-500`}>{it.d}</td></tr>)}
            {s.missing > s.items.length && <tr><td colSpan={4} className={`${TD} text-slate-500 italic`}>… and {s.missing - s.items.length} more</td></tr>}
          </tbody></table></div>
        </div>
      )}
    </div>
  );
}

export function FgHealth() {
  const qc = useQueryClient();
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ["fg-health"], queryFn: async () => (await api.get("/master/fg-health")).data });
  const d = q.data;
  const backfill = async () => {
    setBusy(true);
    try {
      const [a, b] = await Promise.all([api.post("/master/fg-backfill"), api.post("/program/fg-backfill")]);
      toast.success(`Backfill done · ${a.data.customers_fixed} customer(s) fixed, ${a.data.records_stamped} record(s) stamped · POs ${b.data.pos_filled}, invoices ${b.data.invoices_filled}`, { duration: 8000 });
      qc.invalidateQueries({ queryKey: ["fg-health"] }); qc.invalidateQueries({ queryKey: ["masters"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="pp-card p-4 space-y-3" data-testid="fg-health-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="pp-label flex items-center gap-1">{d && d.missing_total === 0 ? <ShieldCheck size={14} className="text-emerald-600" /> : <ShieldAlert size={14} className="text-amber-600" />} FG Code health check</div>
          <div className="text-xs text-slate-500">FG Code is the single product key from Plan to Dispatch. {d ? (d.missing_total === 0 ? "Every row is mapped." : <><b className="text-amber-800" data-testid="fg-health-total">{d.missing_total}</b> row(s) still lack an FG code.</>) : "Loading…"}</div>
        </div>
        <div className="flex gap-2">
          <button className="pp-btn-ghost h-8" onClick={() => q.refetch()} data-testid="fg-health-refresh"><RefreshCw size={13} /> Re-check</button>
          <button className="pp-btn-primary h-8" disabled={busy} onClick={backfill} title="Fill FG codes everywhere from Customer Master (models, POs, invoices, records)" data-testid="fg-health-backfill"><Wand2 size={13} /> {busy ? "Filling…" : "Backfill from Master"}</button>
        </div>
      </div>
      {d && <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">{d.sections.map((s) => <Section key={s.key} s={s} open={!!open[s.key]} toggle={() => setOpen((x) => ({ ...x, [s.key]: !x[s.key] }))} />)}</div>}
      {q.isError && <div className="text-xs text-rose-700">{errMsg(q.error)}</div>}
    </div>
  );
}
